
import { Prop, Vue, Watch } from 'vue-property-decorator'
import Component from 'vue-class-component'
import axios from 'axios'

const SearchComponent = Component({
  components: {}
})

type SearchResult = {
  id: number,
  reference?: string|null,
  number?: string|number|null,
  name?: string|null,
  external_ref?: string|null,
  title?: string|null,
  user_email?: string|null,
  user_firstname?: string|null,
  user_lastname?: string|null,
  result_label?: string|null,
  status_label?: {
    name?: string|null
  }|null
}

type SearchInputRef = Vue & {
  focus?: () => void,
  $el: HTMLElement
}

@SearchComponent
export default class SearchWidget extends Vue {
  @Prop({ type: Boolean }) fullWidth!:boolean
  @Prop({ type: Boolean, default: false }) autoFocus!:boolean

  $refs!: {
    root: HTMLElement,
    searchInput: SearchInputRef
  }

  searchTerm = ''
  results:Record<string, SearchResult[]> = {}
  showResults = false
  searchTimer = 0
  blurTimer = 0
  loading = false
  dropdownHovering = false

  get hasResults () : boolean {
    return Object.values(this.results).some((results) => Array.isArray(results) && results.length > 0)
  }

  mounted () : void {
    document.addEventListener('mousedown', this.handleDocumentMouseDown)

    if (this.autoFocus) {
      this.focusInput()
    }
  }

  beforeDestroy () : void {
    document.removeEventListener('mousedown', this.handleDocumentMouseDown)
    clearTimeout(this.searchTimer)
    clearTimeout(this.blurTimer)
  }

  @Watch('searchTerm')
  onSearchTermChange () : void {
    if (this.searchTerm.length <= 3) {
      this.results = {}
      this.showResults = false
    }
  }

  focusInput () : void {
    this.$nextTick(() => {
      window.setTimeout(() => {
        if (this.$refs.searchInput && typeof this.$refs.searchInput.focus === 'function') {
          this.$refs.searchInput.focus()
          return
        }

        const input = this.$refs.searchInput && this.$refs.searchInput.$el
          ? this.$refs.searchInput.$el.querySelector('input')
          : null

        if (input) {
          input.focus()
        }
      }, 100)
    })
  }

  handleFocus () : void {
    clearTimeout(this.blurTimer)

    if (this.searchTerm.length >= 3) {
      this.showResults = true
    }
  }

  handleBlur () : void {
    clearTimeout(this.blurTimer)

    this.blurTimer = window.setTimeout(() => {
      if (!this.dropdownHovering) {
        this.showResults = false
      }
    }, 150)
  }

  handleDocumentMouseDown (event:MouseEvent) : void {
    if (this.$refs.root && event.target instanceof Node && !this.$refs.root.contains(event.target)) {
      this.showResults = false
    }
  }

  handleSearch () : void {
    if (this.searchTerm.length >= 3) {
      clearTimeout(this.searchTimer)

      this.searchTimer = setTimeout(() => {
        this.loading = true
        axios.post(process.env.VUE_APP_API_URL + '/search', { searchTerm: this.searchTerm }).then((res) => {
          this.results = this.normalizeResults(res.data)
          this.showResults = true
        }).finally(() => {
          this.loading = false
        })
      }, 500)
    }
  }

  normalizeResults (data:unknown) : Record<string, SearchResult[]> {
    const normalized:Record<string, SearchResult[]> = {}

    if (!data || Array.isArray(data) || typeof data !== 'object') {
      return normalized
    }

    const entries = data as Record<string, unknown>

    Object.entries(entries).forEach(([group, results]) => {
      if (Array.isArray(results) && results.length > 0) {
        normalized[group] = results as SearchResult[]
      }
    })

    return normalized
  }

  getResultText (group:string, result:SearchResult) : string {
    switch (group) {
      case 'Projecten':
        return `${result.name} (${result.reference})`
      case 'Bouwnummers':
        return `Bouwnummer ${result.number} (${result.reference})`
      case 'Kopers':
        return `${result.name}`
      case 'Opties':
        return `${result.external_ref} - ${result.name}`
      case 'Orders':
        return this.getOrderText(result)
      case 'Servicemeldingen':
        return `Servicemelding #${result.id} - ${result.title}`
      case 'Keuringen':
        return this.getSurveyText(result)
      default:
        return `#${result.id}`
    }
  }

  getOrderText (result:SearchResult) : string {
    const userName = [result.user_firstname, result.user_lastname].filter(Boolean).join(' ')
    const user = userName || result.user_email

    return user ? `Order #${result.id} - ${user}` : `Order #${result.id}`
  }

  getSurveyText (result:SearchResult) : string {
    const status = result.status_label && result.status_label.name ? result.status_label.name : result.result_label

    return status ? `Keuring #${result.id} - ${status}` : `Keuring #${result.id}`
  }

  goToItem (group:string, id:number) : void {
    switch (group) {
      case 'Projecten':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'projectShow', params: { projectId: id.toString() } }).href, '_blank')
        break
      case 'Bouwnummers':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'buildingShow', params: { buildingId: id.toString() } }).href, '_blank')
        break
      case 'Kopers':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'customerShow', params: { customerId: id.toString() } }).href, '_blank')
        break
      case 'Opties':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'optionShow', params: { optionId: id.toString() } }).href, '_blank')
        break
      case 'Orders':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'orderShow', params: { orderId: id.toString() } }).href, '_blank')
        break
      case 'Servicemeldingen':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'issueEdit', params: { issueId: id.toString() } }).href, '_blank')
        break
      case 'Keuringen':
        this.showResults = false
        window.open(this.$router.resolve({ name: 'surveyShow', params: { surveyId: id.toString() } }).href, '_blank')
        break
      default:
        break
    }
  }
}
